import React, { useState, useCallback } from 'react';
import ColorWheel from '../components/ColorWheel';
import BrightnessSlider from '../components/BrightnessSlider';
import { Button, Typography, Box, Paper } from '@mui/material';
import { getRgbaColor, getTextColor } from '../utils/colorUtils';

const MainColorWheel = ({
  initialLedColor = 'rgb(255, 255, 255)', // Default white
  initialBrightness = 100, // Default full brightness
  onSendColor,
}) => {
  const [ledColor, setLEDColor] = useState(initialLedColor);
  const [brightness, setBrightness] = useState(initialBrightness);
  const [responses, setResponses] = useState([]);

  // Parse rgb(r,g,b) string to numeric values
  const parseRgb = (color) => {
    if (typeof color !== 'string') {
      return { red: 255, green: 255, blue: 255 };
    }

    // Handle hex #RRGGBB
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const bigint = parseInt(hex, 16);
      return {
        red: (bigint >> 16) & 255,
        green: (bigint >> 8) & 255,
        blue: bigint & 255,
      };
    }

    // Handle rgb(r,g,b)
    const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
      return {
        red: parseInt(match[1], 10),
        green: parseInt(match[2], 10),
        blue: parseInt(match[3], 10),
      };
    }

    // Default fallback
    return { red: 255, green: 255, blue: 255 };
  };

  const updateColor = useCallback(
    (color, brightnessValue = brightness) => {
      setLEDColor(color);
      setBrightness(brightnessValue);

      // Base RGB (from wheel, full brightness)
      const baseRgb = parseRgb(color);

      // Scale with brightness percentage
      const scale = brightnessValue / 100;
      const rgb = {
        red: Math.round(baseRgb.red * scale),
        green: Math.round(baseRgb.green * scale),
        blue: Math.round(baseRgb.blue * scale),
      };

      // console.log('Chosen color (scaled with brightness):', rgb);

      if (
        rgb.red >= 0 &&
        rgb.red <= 255 &&
        rgb.green >= 0 &&
        rgb.green <= 255 &&
        rgb.blue >= 0 &&
        rgb.blue <= 255
      ) {
        onSendColor({ ...rgb, brightness: brightnessValue });
      } else {
        setResponses((prev) => [...prev, 'Error: Invalid RGB color values']);
      }
    },
    [brightness, onSendColor]
  );

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h5" align="center" gutterBottom>
        Main Color Wheel
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          marginTop: 2,
        }}
      >
        {/* Enlarged Color Wheel */}
        <Box sx={{ flex: 1, maxWidth: '65%' }}>
          <ColorWheel color={ledColor} onChange={updateColor} />
        </Box>

        {/* Vertical Brightness Slider */}
        <Box sx={{ height: '70%', display: 'flex', alignItems: 'center' }}>
          <BrightnessSlider
            value={brightness}
            onChange={(val) => updateColor(ledColor, val)} // 👈 call updateColor with current color + new brightness
          />
        </Box>
      </Box>
      <Button
        variant="contained"
        color="secondary"
        fullWidth
        onClick={() => updateColor('rgb(0,0,0)', 0)}
        sx={{ marginTop: 2 }}
      >
        Clear LEDs
      </Button>
      <Paper
        sx={{
          padding: 2,
          marginTop: 3,
          backgroundColor: getRgbaColor(ledColor, brightness),
          color: getTextColor(ledColor, brightness),
          border: '1px solid #ddd',
        }}
      >
        <Typography>Brightness: {brightness}%</Typography>
        <Typography>Color: {getRgbaColor(ledColor, brightness)}</Typography>
      </Paper>
    </Box>
  );
};

export default MainColorWheel;
